import { GAME_RULES, buildingLimit } from './rules.js';
import { hydrateMonetization, monetizationDefaults } from './monetization.js';
import { adventureDefaults, ensureAdventure, researchBonus, sanitizeProgressionState, stageStatus, troopAdventureBonuses } from './adventure.js';

export const BUILDINGS = {
  hall: { kind: 0, name: 'Spirit Hall', model: 'SpiritHall_L1_Map03.glb', size: 5.4, hp: 900, icon: '🏯', role: 'หัวใจของหมู่บ้าน', unlockHall: 1 },
  mint: { kind: 1, name: 'Sun Mint', model: 'SunMint_L1.glb', size: 3.7, hp: 360, icon: '🪙', role: 'ผลิตทอง', unlockHall: 1 },
  timber: { kind: 2, name: 'Timber Lodge', model: 'TimberLodge_L1.glb', size: 3.8, hp: 390, icon: '🪵', role: 'ผลิตไม้จิตวิญญาณ', unlockHall: 1 },
  arrow: { kind: 3, name: 'Arrow Perch', model: 'ArrowPerch_L1.glb', size: 3.5, hp: 430, icon: '🏹', role: 'ยิงป้องกันระยะไกล', defense: 13, unlockHall: 2 },
  rally: { kind: 4, name: 'Rally Hut', model: 'RallyHut_L1.glb', size: 4.1, hp: 460, icon: '🛡️', role: 'ฝึกและจัดกำลังพล', unlockHall: 1 },
  vault: { kind: 5, name: 'Gold Vault', model: 'GoldVault_L1.glb', size: 3.6, hp: 520, icon: '🔐', role: 'เพิ่มความจุทอง', unlockHall: 2 },
  dew: { kind: 6, name: 'Dew Reservoir', model: 'DewReservoir_L1.glb', size: 3.7, hp: 440, icon: '💎', role: 'เก็บน้ำค้างฟ้า', unlockHall: 2 },
  workshop: { kind: 7, name: 'Workshop', model: 'Workshop_L1.glb', size: 4, hp: 490, icon: '⚒️', role: 'พัฒนาอุปกรณ์นักรบ', unlockHall: 3 },
  hero: { kind: 8, name: 'Hero Lodge', model: 'HeroLodge_L1.glb', size: 4.2, hp: 560, icon: '🦁', role: 'บ้านฮีโร่', unlockHall: 3 },
  dock: { kind: 9, name: 'Sky Dock', model: 'SkyDock_L1.glb', size: 4.7, hp: 520, icon: '⛵', role: 'ออกสำรวจเกาะใหม่', unlockHall: 2 },
  totem: { kind: 10, name: 'Wind Totem', model: 'WindTotem_L1.glb', size: 3.3, hp: 410, icon: '🌀', role: 'เสริมพลังตราประทับ', unlockHall: 4 },
  bell: { kind: 11, name: 'Spirit Bell', model: 'SpiritBell_L1.glb', size: 3.4, hp: 420, icon: '🔔', role: 'เตือนภัยรอบเกาะ', unlockHall: 3 },
  grove: { kind: 12, name: 'Spirit Grove', model: 'SpiritGrove_L1.glb', size: 3.7, hp: 390, icon: '🌳', role: 'ผลิตไม้วิญญาณ', unlockHall: 2 },
  well: { kind: 13, name: 'Cloud Well', model: 'CloudWell_L1.glb', size: 3.6, hp: 400, icon: '💧', role: 'ผลิตน้ำค้างฟ้า', unlockHall: 2 },
  guardian: { kind: 14, name: 'Cloud Lion Guardian', model: 'CloudLionGuardian_L1.glb', size: 3.8, hp: 620, icon: '🦁', role: 'ผู้พิทักษ์ป้องกันทางอากาศ', defense: 18, unlockHall: 4 },
  builder: { kind: 15, name: 'Builder Hut', model: 'BuilderHut_L1.glb', size: 3.1, hp: 320, icon: '👷', role: 'ลดเวลาก่อสร้างของช่างทุกคน', unlockHall: 2 },
  laboratory: { kind: 16, name: 'Spirit Laboratory', model: 'SpiritLaboratory_L1.glb', size: 4, hp: 480, icon: '⚗️', role: 'วิจัยพลังวิญญาณและ VFX', unlockHall: 4 },
  scout: { kind: 17, name: 'Scout Pavilion', model: 'ScoutPavilion_L1.glb', size: 3.6, hp: 390, icon: '🔭', role: 'สอดแนมและเพิ่มรางวัลการบุก', unlockHall: 3 }
};

export const TROOPS = {
  runner: { name: 'นักธนูเมฆ', icon: '🏹', model: 'CloudRunner_Rigged.glb', housing: 2, hp: 100, damage: 17, speed: 3.6, cost: 70, unlockHall: 1, targetPreference: 'resource' },
  smith: { name: 'ผู้พิทักษ์ค้อน', icon: '🔨', model: 'ThunderSmith_Rigged.glb', housing: 5, hp: 430, damage: 31, speed: 2.2, cost: 190, unlockHall: 2, targetPreference: 'defense', firstDefenseStrikeMultiplier: 2.5 },
  lotus: { name: 'ผู้เยียวยาบัว', icon: '🪷', model: 'LotusKeeper_Rigged.glb', housing: 6, hp: 210, damage: 15, speed: 2.5, cost: 230, unlockHall: 2, healer: true, targetPreference: 'friendly' },
  ember: { name: 'นักจู่โจมเพลิง', icon: '🔥', model: 'EmberGlider_Rigged.glb', housing: 4, hp: 170, damage: 27, speed: 3.1, cost: 165, unlockHall: 3, targetPreference: 'backline', vaultCooldownMs: 8000 }
};

export const RARITIES = {
  C: { name: 'Common', rank: 0, color: '#8fa3ad', multiplier: 1, shardReward: 10 },
  R: { name: 'Rare', rank: 1, color: '#2cb9e8', multiplier: 1.08, shardReward: 20 },
  SR: { name: 'Super Rare', rank: 2, color: '#9c62f2', multiplier: 1.16, shardReward: 45 },
  UR: { name: 'Ultimate Rare', rank: 3, color: '#ffb629', multiplier: 1.24, shardReward: 100 }
};

export const CHARACTERS = {
  cloud_runner: { name: 'Cloud Runner', title: 'นักล่าเมฆ', rarity: 'C', type: 'runner', model: 'CloudRunner_Rigged.glb', icon: '🏹', hp: 100, damage: 17, speed: 3.65 },
  thunder_smith: { name: 'Thunder Smith', title: 'ช่างตีสายฟ้า', rarity: 'C', type: 'smith', model: 'ThunderSmith_Rigged.glb', icon: '🔨', hp: 430, damage: 31, speed: 2.2 },
  lotus_keeper: { name: 'Lotus Keeper', title: 'ผู้รักษาดอกบัว', rarity: 'C', type: 'lotus', model: 'LotusKeeper_Rigged.glb', icon: '🪷', hp: 210, damage: 15, speed: 2.55, healer: true },
  ember_glider: { name: 'Ember Glider', title: 'ปีกเพลิงน้อย', rarity: 'C', type: 'ember', model: 'EmberGlider_Rigged.glb', icon: '🔥', hp: 170, damage: 27, speed: 3.15 },
  crosswind_captain: { name: 'Crosswind Captain', title: 'กัปตันลมตัด', rarity: 'R', type: 'runner', model: 'CrosswindCaptain_Rigged.glb', icon: '🌪️', hp: 145, damage: 23, speed: 3.8 },
  gearwing_tinkerer: { name: 'Gearwing Tinkerer', title: 'ช่างปีกกล', rarity: 'R', type: 'ember', model: 'GearwingTinkerer_Rigged.glb', icon: '⚙️', hp: 210, damage: 35, speed: 3.25 },
  tidewall_guardian: { name: 'Tidewall Guardian', title: 'โล่คลื่นสมุทร', rarity: 'R', type: 'smith', model: 'TidewallGuardian_Rigged.glb', icon: '🌊', hp: 570, damage: 34, speed: 2.15 },
  silver_lance: { name: 'Silver Lance', title: 'หอกจันทรา', rarity: 'R', type: 'runner', model: 'SilverLance_Rigged.glb', icon: '🗡️', hp: 180, damage: 31, speed: 3.45 },
  elara_venn: { name: 'Elara Venn', title: 'ดวงตาแห่งวายุ', rarity: 'SR', type: 'runner', model: 'ElaraVenn_Rigged.glb', icon: '🦅', hp: 255, damage: 45, speed: 4.05 },
  verdant_caller: { name: 'Verdant Caller', title: 'เสียงเรียกพฤกษา', rarity: 'SR', type: 'lotus', model: 'VerdantCaller_Rigged.glb', icon: '🌿', hp: 330, damage: 28, speed: 2.75, healer: true },
  auric_vale: { name: 'Auric Vale', title: 'ราชันสุริยคราส', rarity: 'UR', type: 'ember', model: 'AuricVale_Rigged.glb', icon: '☀️', hp: 480, damage: 74, speed: 3.7 },
  gilded_vanguard: { name: 'Gilded Vanguard', title: 'ปราการทองคำ', rarity: 'UR', type: 'smith', model: 'GildedVanguard_Rigged.glb', icon: '🦁', hp: 1050, damage: 62, speed: 2.35 },
  cloudreef_sentinel: { name: 'Cloudreef Sentinel', title: 'โล่ปะการังเมฆ', rarity: 'SR', type: 'smith', model: 'cloudreef-sentinel.glb', icon: '🪸', hp: 720, damage: 41, speed: 2.1 },
  lotus_spark_alchemist: { name: 'Lotus Spark Alchemist', title: 'นักปรุงประกายบัว', rarity: 'R', type: 'lotus', model: 'lotus-spark-alchemist.glb', icon: '🧪', hp: 280, damage: 24, speed: 2.8, healer: true },
  tempest_falconer: { name: 'Tempest Falconer', title: 'เหยี่ยววายุ', rarity: 'SR', type: 'runner', model: 'tempest-falconer.glb', icon: '🦅', hp: 275, damage: 48, speed: 4.2 },
  ember_kinnari: { name: 'Ember Kinnari', title: 'กินรีเพลิง', rarity: 'SR', type: 'ember', model: 'ember-kinnari.glb', icon: '🪽', hp: 310, damage: 52, speed: 3.65 },
  dewdrop_oracle: { name: 'Dewdrop Oracle', title: 'เทพพยากรณ์หยาดฟ้า', rarity: 'UR', type: 'lotus', model: 'dewdrop-oracle.glb', icon: '🔮', hp: 520, damage: 46, speed: 2.9, healer: true },
  gale_banner_captain: { name: 'Gale Banner Captain', title: 'แม่ทัพธงวายุ', rarity: 'UR', type: 'ember', model: 'gale-banner-captain.glb', icon: '🚩', hp: 610, damage: 78, speed: 3.55 }
};

export const SUMMON_ODDS = [
  { rarity: 'UR', chance: 3 },
  { rarity: 'SR', chance: 11 },
  { rarity: 'R', chance: 30 },
  { rarity: 'C', chance: 56 }
];

const STORAGE_KEY = 'clash-of-stamp-web-v3';

export const DAILY_MISSIONS = [
  { id: 'collect', title: 'เก็บพลังจากเมือง', detail: 'เก็บผลผลิต 2 ครั้ง', target: 2, reward: { gold: 260, dew: 10 }, icon: 'spark' },
  { id: 'train', title: 'เตรียมกองพิทักษ์', detail: 'ฝึกนักรบ 2 หน่วย', target: 2, reward: { wood: 320 }, icon: 'shield' },
  { id: 'battle', title: 'เปิดเส้นทางเมฆ', detail: 'ออกศึก 1 ครั้ง', target: 1, reward: { gold: 420, wood: 260, dew: 20 }, icon: 'swords' }
];

export const ACHIEVEMENTS = [
  { id: 'architect', title: 'สถาปนิกแห่งเวหา', detail: 'สร้างอาคารสะสม 5 หลัง', target: 5, counter: 'build', reward: { gold: 900, dew: 45 } },
  { id: 'veteran', title: 'กัปตันผู้ชนะ', detail: 'ชนะการต่อสู้สะสม 3 ครั้ง', target: 3, counter: 'win', reward: { gold: 1200, wood: 800, dew: 60 } },
  { id: 'collector', title: 'ผู้ปลุกวิญญาณ', detail: 'อัญเชิญตัวละครสะสม 10 ครั้ง', target: 10, counter: 'summon', reward: { dew: 180, tickets: 1 } }
];

function dayKey(now = Date.now()) {
  const date = new Date(now);
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

function progressionDefaults(now = Date.now()) {
  return {
    dayKey: dayKey(now),
    daily: { collect: 0, train: 0, battle: 0 },
    claimedMissions: [],
    chestClaimed: false,
    lifetime: { collect: 0, train: 0, battle: 0, build: 0, upgrade: 0, summon: 0, win: 0 },
    claimedAchievements: []
  };
}

export function freshState() {
  return {
    version: 3,
    playerName: 'Stamp',
    level: 1,
    xp: 0,
    gold: 1200,
    spiritWood: 900,
    skyDew: 60,
    summonTickets: 1,
    summonPity: 0,
    srPity: 0,
    territoryLevel: 0,
    lastCollectedAt: Date.now() - 1000 * 60 * 6,
    buildings: [{ id: 'hall-1', type: 'hall', level: 1, x: 0, z: 0 }],
    army: { runner: 0, smith: 0, lotus: 0, ember: 0 },
    heroes: {},
    activeHeroes: {},
    battleWins: 0,
    recentOpponents: {},
    monetization: monetizationDefaults(),
    adventure: adventureDefaults(),
    constructionQueue: [],
    baseLayouts: [],
    preferences: { quality: 'auto', analytics: false, haptics: true },
    progression: progressionDefaults(),
    tutorialStep: 0,
    tutorialComplete: false,
    online: { playerId: '', authToken: '', friendCode: '', syncedAt: 0 }
  };
}

export function normalizeTutorialVillage(state) {
  if (state.tutorialComplete) return state;
  state.tutorialStep = Math.max(0, Math.min(9, Math.floor(Number(state.tutorialStep) || 0)));
  if (state.tutorialStep > 1) return state;

  // A brand-new village has exactly one gameplay building. Older prototype/cloud
  // saves sometimes contained a populated demo base while still being on tutorial
  // step 0; keeping those buildings makes every "new" account look pre-built.
  const tutorialStep = state.tutorialStep;
  const accountData = {
    playerName: state.playerName,
    online: state.online,
    preferences: state.preferences,
    monetization: state.monetization
  };
  Object.assign(state, freshState(), accountData, { tutorialStep });
  return state;
}

export function hydrateState(stored) {
  if (!stored || !Array.isArray(stored.buildings)) return freshState();
  const defaults = freshState();
  const hydrated = {
    ...defaults, ...stored,
    army: { ...defaults.army, ...(stored.army || {}) },
    heroes: { ...(stored.heroes || {}) },
    activeHeroes: { ...(stored.activeHeroes || {}) },
    recentOpponents: { ...(stored.recentOpponents || {}) },
    monetization: hydrateMonetization(stored.monetization),
    adventure: { ...adventureDefaults(), ...(stored.adventure || {}) },
    constructionQueue: [...(stored.constructionQueue || [])],
    baseLayouts: [...(stored.baseLayouts || [])],
    preferences: { ...defaults.preferences, ...(stored.preferences || {}) },
    online: { ...defaults.online, ...(stored.online || {}) },
    progression: {
      ...defaults.progression,
      ...(stored.progression || {}),
      daily: { ...defaults.progression.daily, ...(stored.progression?.daily || {}) },
      lifetime: { ...defaults.progression.lifetime, ...(stored.progression?.lifetime || {}) },
      claimedMissions: [...(stored.progression?.claimedMissions || [])],
      claimedAchievements: [...(stored.progression?.claimedAchievements || [])]
    }
  };
  ensureAdventure(hydrated);
  normalizeTutorialVillage(hydrated);
  return sanitizeProgressionState(hydrated);
}

export function ensureDailyProgress(state, now = Date.now()) {
  if (!state.progression) state.progression = progressionDefaults(now);
  const nextDay = dayKey(now);
  if (state.progression.dayKey !== nextDay) {
    state.progression.dayKey = nextDay;
    state.progression.daily = { collect: 0, train: 0, battle: 0 };
    state.progression.claimedMissions = [];
    state.progression.chestClaimed = false;
  }
  return state.progression;
}

export function recordProgress(state, event, amount = 1) {
  const progression = ensureDailyProgress(state);
  if (event in progression.daily) progression.daily[event] = Math.max(0, progression.daily[event] + amount);
  progression.lifetime[event] = Math.max(0, (progression.lifetime[event] || 0) + amount);
  return progression;
}

function grantReward(state, reward) {
  state.gold += reward.gold || 0;
  state.spiritWood += reward.wood || 0;
  state.skyDew += reward.dew || 0;
  state.summonTickets += reward.tickets || 0;
  return { ...reward };
}

export function dailyMissionStatus(state) {
  const progression = ensureDailyProgress(state);
  return DAILY_MISSIONS.map(mission => ({
    ...mission,
    progress: Math.min(mission.target, progression.daily[mission.id] || 0),
    ready: (progression.daily[mission.id] || 0) >= mission.target,
    claimed: progression.claimedMissions.includes(mission.id)
  }));
}

export function claimDailyMission(state, id) {
  const progression = ensureDailyProgress(state);
  const mission = DAILY_MISSIONS.find(item => item.id === id);
  if (!mission) return { ok: false, reason: 'ไม่พบภารกิจนี้' };
  if (progression.claimedMissions.includes(id)) return { ok: false, reason: 'รับรางวัลแล้ว' };
  if ((progression.daily[id] || 0) < mission.target) return { ok: false, reason: 'ภารกิจยังไม่สำเร็จ' };
  progression.claimedMissions.push(id);
  return { ok: true, mission, reward: grantReward(state, mission.reward) };
}

export function claimDailyChest(state) {
  const progression = ensureDailyProgress(state);
  if (progression.chestClaimed) return { ok: false, reason: 'เปิดหีบประจำวันแล้ว' };
  if (progression.claimedMissions.length < DAILY_MISSIONS.length) return { ok: false, reason: 'รับรางวัลภารกิจให้ครบก่อน' };
  progression.chestClaimed = true;
  const reward = { gold: 750, wood: 520, dew: 80, tickets: 1 };
  return { ok: true, reward: grantReward(state, reward) };
}

export function achievementStatus(state) {
  const progression = ensureDailyProgress(state);
  return ACHIEVEMENTS.map(achievement => ({
    ...achievement,
    progress: Math.min(achievement.target, progression.lifetime[achievement.counter] || 0),
    ready: (progression.lifetime[achievement.counter] || 0) >= achievement.target,
    claimed: progression.claimedAchievements.includes(achievement.id)
  }));
}

export function claimAchievement(state, id) {
  const progression = ensureDailyProgress(state);
  const achievement = ACHIEVEMENTS.find(item => item.id === id);
  if (!achievement) return { ok: false, reason: 'ไม่พบความสำเร็จนี้' };
  if (progression.claimedAchievements.includes(id)) return { ok: false, reason: 'รับรางวัลแล้ว' };
  if ((progression.lifetime[achievement.counter] || 0) < achievement.target) return { ok: false, reason: 'ยังทำไม่สำเร็จ' };
  progression.claimedAchievements.push(id);
  return { ok: true, achievement, reward: grantReward(state, achievement.reward) };
}

export function recommendedAction(state) {
  if (!state.tutorialComplete) return { label: 'ทำบทสอนต่อ', detail: 'ลูมี่กำลังรอคุณอยู่', panel: '' };
  const ready = dailyMissionStatus(state).find(item => item.ready && !item.claimed);
  if (ready) return { label: 'รับรางวัลภารกิจ', detail: ready.title, panel: 'missions' };
  if (production(state).gold + production(state).wood > 35) return { label: 'เก็บผลผลิต', detail: 'ทรัพยากรในเมืองพร้อมแล้ว', action: 'collect' };
  if (!Object.keys(state.heroes).length) return { label: 'อัญเชิญหัวหน้ากอง', detail: 'ปลดล็อกนักรบคนแรก', panel: 'summon' };
  if (!Object.values(state.army).some(Boolean)) return { label: 'ฝึกนักรบ', detail: 'เตรียมกำลังสำหรับออกศึก', panel: 'army' };
  const pending = dailyMissionStatus(state).find(item => !item.claimed);
  if (pending) return { label: pending.title, detail: `${pending.progress}/${pending.target}`, panel: pending.id === 'battle' ? '' : pending.id === 'train' ? 'army' : '' };
  const nextStage = stageStatus(state).find(stage => stage.unlocked && !stage.stars);
  if (nextStage && ensureAdventure(state).energy >= nextStage.energy) return { label: 'Campaign ถัดไป', detail: nextStage.title, panel: 'adventure' };
  return { label: 'ออกสำรวจเกาะคู่แข่ง', detail: 'รับดาวและทรัพยากร', action: 'battle' };
}

export function loadState() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (stored && Array.isArray(stored.buildings)) return hydrateState(stored);
    const next = freshState();
    const legacy = JSON.parse(localStorage.getItem('clash-of-stamp-web-v1'));
    if (legacy?.online?.playerId && legacy?.online?.authToken) next.online = { ...next.online, ...legacy.online };
    return next;
  } catch {
    return freshState();
  }
}

export function saveState(state) { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }
export function compactNumber(value) { return new Intl.NumberFormat('th-TH', { maximumFractionDigits: 0 }).format(Math.max(0, Math.floor(value))); }
export function hallLevel(state) { return state.buildings.find(item => item.type === 'hall')?.level || 1; }

export function upgradeCost(building) {
  const scale = Math.pow(1.68, building.level - 1);
  const base = building.type === 'hall' ? 620 : 260;
  return { gold: Math.round(base * scale / 10) * 10, wood: Math.round(base * .7 * scale / 10) * 10, dew: building.level >= 2 ? Math.round(15 * scale) : 0 };
}

export function canAfford(state, cost) {
  return state.gold >= (cost.gold || 0) && state.spiritWood >= (cost.wood || 0) && state.skyDew >= (cost.dew || 0);
}

export function spend(state, cost) {
  state.gold -= cost.gold || 0;
  state.spiritWood -= cost.wood || 0;
  state.skyDew -= cost.dew || 0;
}

export function builderCapacity(state) {
  return 2 + (state.monetization?.entitlements?.includes('builder_slot_3') ? 1 : 0);
}

export function constructionDuration(state, level = 1, kind = 'build') {
  const hutLevels = state.buildings.filter(item => item.type === 'builder' && !item.underConstruction).reduce((sum, item) => sum + item.level, 0);
  const reduction = Math.min(.45, hutLevels * .15);
  const base = kind === 'upgrade' ? 75 * Math.max(1, level) : 45;
  return Math.round(base * (1 - reduction) * 1000);
}

export function processConstruction(state, now = Date.now()) {
  state.constructionQueue ||= [];
  const completed = [];
  state.constructionQueue = state.constructionQueue.filter(job => {
    if (job.readyAt > now) return true;
    const building = state.buildings.find(item => item.id === job.buildingId);
    if (building) {
      if (job.kind === 'upgrade') building.level = Math.max(building.level, job.targetLevel || building.level + 1);
      delete building.underConstruction;
      delete building.readyAt;
      completed.push(building);
    }
    return false;
  });
  return completed;
}

export function builderStatus(state, now = Date.now()) {
  const total = builderCapacity(state);
  const busy = (state.constructionQueue || []).filter(job => job.readyAt > now).length;
  return { total, busy, available: Math.max(0, total - busy) };
}

export function upgradeBuilding(state, id, options = {}) {
  processConstruction(state);
  const building = state.buildings.find(item => item.id === id);
  if (!building || building.level >= 5) return { ok: false, reason: 'อาคารถึงเลเวลสูงสุดแล้ว' };
  if (!state.tutorialComplete && !(state.tutorialStep === 7 && building.type === 'hall')) return { ok: false, reason: 'ทำตามขั้นตอนบทสอนกับลูมี่ก่อน' };
  if (building.underConstruction) return { ok: false, reason: 'อาคารนี้กำลังก่อสร้างอยู่' };
  if (!options.instant && builderStatus(state).available <= 0) return { ok: false, reason: 'ช่างทุกคนกำลังทำงานอยู่' };
  const cost = upgradeCost(building);
  if (!canAfford(state, cost)) return { ok: false, reason: 'ทรัพยากรยังไม่พอ' };
  spend(state, cost);
  const targetLevel = building.level + 1;
  if (options.instant) building.level = targetLevel;
  else {
    building.underConstruction = 'upgrade';
    building.readyAt = Date.now() + constructionDuration(state, targetLevel, 'upgrade');
    state.constructionQueue.push({ buildingId: building.id, kind: 'upgrade', targetLevel, readyAt: building.readyAt });
  }
  state.xp += building.type === 'hall' ? 120 : 45;
  state.level = Math.max(state.level, 1 + Math.floor(state.xp / 180));
  return { ok: true, building, cost, queued: !options.instant, targetLevel };
}

export function storageCaps(state) {
  const caps = { ...GAME_RULES.economy.baseStorage };
  for (const building of state.buildings.filter(item => !item.underConstruction)) {
    if (building.type === 'vault') caps.gold += GAME_RULES.economy.vaultPerLevel * building.level;
    if (building.type === 'timber') caps.wood += GAME_RULES.economy.timberStoragePerLevel * building.level;
    if (building.type === 'dew') caps.dew += GAME_RULES.economy.dewStoragePerLevel * building.level;
  }
  const multiplier = 1 + researchBonus(state, 'storage');
  return { gold: Math.round(caps.gold * multiplier), wood: Math.round(caps.wood * multiplier), dew: Math.round(caps.dew * multiplier) };
}

export function production(state, now = Date.now()) {
  const minutes = Math.min(GAME_RULES.economy.offlineMinutes, Math.max(0, now - state.lastCollectedAt) / 60000);
  const ready = state.buildings.filter(item => !item.underConstruction);
  const mint = ready.filter(b => b.type === 'mint').reduce((sum, b) => sum + b.level, 0);
  const timber = ready.filter(b => b.type === 'timber' || b.type === 'grove').reduce((sum, b) => sum + b.level, 0);
  const wells = ready.filter(b => b.type === 'well').reduce((sum, b) => sum + b.level, 0);
  const multiplier = 1 + researchBonus(state, 'production');
  return { gold: Math.floor(minutes * mint * 5 * multiplier), wood: Math.floor(minutes * timber * 4 * multiplier), dew: Math.floor(minutes * wells * 1.4 / 8 * multiplier) };
}

export function collectProduction(state) {
  const value = production(state);
  const caps = storageCaps(state);
  const granted = {
    gold: Math.max(0, Math.min(value.gold, caps.gold - state.gold)),
    wood: Math.max(0, Math.min(value.wood, caps.wood - state.spiritWood)),
    dew: Math.max(0, Math.min(value.dew, caps.dew - state.skyDew))
  };
  state.gold += granted.gold;
  state.spiritWood += granted.wood;
  state.skyDew += granted.dew;
  state.lastCollectedAt = Date.now();
  return { ...granted, capped: granted.gold < value.gold || granted.wood < value.wood || granted.dew < value.dew };
}

export const EXPANSIONS = [
  { name: 'เกาะสายลม', requiredHall: 2, cost: { gold: 1200, wood: 900, dew: 60 }, reward: 'พื้นที่ใหม่ + Sky Dock' },
  { name: 'สะพานเมฆา', requiredHall: 3, cost: { gold: 3200, wood: 2400, dew: 160 }, reward: 'พื้นที่กองทัพ + Hero Lodge' },
  { name: 'ยอดผาคริสตัล', requiredHall: 4, cost: { gold: 6800, wood: 4900, dew: 380 }, reward: 'เกาะที่สอง + Wind Totem' }
];

const EXPANSION_BUILDINGS = [
  { id: 'dock-1', type: 'dock', level: 1, x: 19.5, z: -2.2 },
  { id: 'hero-1', type: 'hero', level: 1, x: 20.5, z: 4.1 },
  { id: 'totem-1', type: 'totem', level: 1, x: 28.3, z: 10.4 }
];

export function expandTerritory(state) {
  const next = EXPANSIONS[state.territoryLevel];
  if (!next) return { ok: false, reason: 'ปลดล็อกพื้นที่ครบแล้ว' };
  if (hallLevel(state) < next.requiredHall) return { ok: false, reason: `ต้องมี Spirit Hall เลเวล ${next.requiredHall}` };
  if (!canAfford(state, next.cost)) return { ok: false, reason: 'ทรัพยากรสำหรับขยายเกาะยังไม่พอ' };
  spend(state, next.cost);
  state.territoryLevel += 1;
  state.buildings.push({ ...EXPANSION_BUILDINGS[state.territoryLevel - 1] });
  state.xp += 160;
  return { ok: true, expansion: next };
}

export function activeCharacter(state, type) {
  const selected = state.activeHeroes[type];
  if (selected && state.heroes[selected]) return { id: selected, ...CHARACTERS[selected], ...state.heroes[selected] };
  const owned = Object.keys(state.heroes).filter(id => CHARACTERS[id]?.type === type);
  owned.sort((a, b) => RARITIES[CHARACTERS[b].rarity].rank - RARITIES[CHARACTERS[a].rarity].rank || (state.heroes[b].level || 1) - (state.heroes[a].level || 1));
  const id = owned[0];
  return id ? { id, ...CHARACTERS[id], ...state.heroes[id] } : null;
}

export function combatTroop(state, type) {
  const troop = TROOPS[type];
  const hero = activeCharacter(state, type);
  if (!troop || (hallLevel(state) < troop.unlockHall && !hero)) return null;
  const rarity = hero ? RARITIES[hero.rarity] : RARITIES.C;
  const rarityBonus = GAME_RULES.leader.rarityBonuses[hero?.rarity || 'C'];
  const levelBonus = Math.min(GAME_RULES.leader.maximumLevelBonus, Math.max(0, (hero?.level || 1) - 1) * GAME_RULES.leader.levelBonusPerLevel);
  const multiplier = 1 + rarityBonus + levelBonus;
  const adventure = troopAdventureBonuses(state, type);
  return {
    ...troop,
    leaderId: hero?.id || '', leaderName: hero?.name || 'ไม่มีหัวหน้ากอง', leaderRarity: hero?.rarity || 'C', rarity: hero?.rarity || 'C',
    level: hero?.level || 1,
    hp: Math.round(troop.hp * multiplier * (1 + adventure.hp)),
    damage: Math.round(troop.damage * multiplier * (1 + adventure.damage)),
    speed: troop.speed * (1 + rarity.rank * .015 + adventure.speed),
    criticalChance: Math.min(.55, .08 + rarity.rank * .04 + adventure.criticalChance),
    healingMultiplier: 1 + adventure.healing,
    perk: rarity.rank === 3 ? 'Legendary Burst · คริติคอลและพลังตราประทับสูงสุด' : rarity.rank === 2 ? 'Spirit Surge · โอกาสคริติคอลสูง' : rarity.rank === 1 ? 'Wind Tempo · เคลื่อนที่เร็วขึ้น' : 'Core Training · สมดุลทุกด้าน',
    stampMultiplier: 1 + rarity.rank * .04,
    auroraTrail: Boolean(state.monetization?.cosmetics?.includes('trail_aurora'))
  };
}

export function trainTroop(state, type) {
  const troop = TROOPS[type];
  if (!troop) return { ok: false, reason: 'ไม่พบนักรบ' };
  if (hallLevel(state) < troop.unlockHall) return { ok: false, reason: `ปลดล็อกที่ Spirit Hall เลเวล ${troop.unlockHall}` };
  if (state.spiritWood < troop.cost) return { ok: false, reason: 'ไม้จิตวิญญาณไม่พอ' };
  const capacity = 12 + state.buildings.filter(b => b.type === 'rally').reduce((sum, b) => sum + b.level * 8, 0);
  const used = Object.entries(state.army).reduce((sum, [key, count]) => sum + TROOPS[key].housing * count, 0);
  if (used + troop.housing > capacity) return { ok: false, reason: 'ค่ายทหารเต็มแล้ว' };
  state.spiritWood -= troop.cost;
  state.army[type] = (state.army[type] || 0) + 1;
  return { ok: true };
}

const BUILD_COSTS = {
  mint: { gold: 320, wood: 180 }, timber: { gold: 380, wood: 340 }, rally: { gold: 420, wood: 300 },
  arrow: { gold: 700, wood: 570 }, vault: { gold: 840, wood: 520 }, dew: { gold: 1000, wood: 760, dew: 40 },
  workshop: { gold: 1320, wood: 1100, dew: 65 }, bell: { gold: 1480, wood: 1200, dew: 80 },
  grove: { gold: 820, wood: 640 }, well: { gold: 960, wood: 720, dew: 35 },
  guardian: { gold: 2600, wood: 1900, dew: 180 }, builder: { gold: 740, wood: 880 },
  laboratory: { gold: 2900, wood: 2300, dew: 260 }, scout: { gold: 1850, wood: 1420, dew: 100 }
};

const BUILD_SLOTS = [[-5.4,-2.5], [4.8,4.2], [5.2,-2.4], [-4.7,4.4], [-8,0], [8,0], [-7.4,-6.2], [7.4,-6.2], [0,7.2], [16.8,-5.2], [22.5,-5], [16.2,5.7], [23,5.8], [18.2,0], [25,0], [29,8], [33,12], [28,14]];
export function buildingCost(type) { return BUILD_COSTS[type] || { gold: 999999 }; }

export function buildBuilding(state, type, options = {}) {
  processConstruction(state);
  const def = BUILDINGS[type];
  const cost = BUILD_COSTS[type];
  if (!def || !cost) return { ok: false, reason: 'อาคารนี้ยังไม่พร้อมสร้าง' };
  const tutorialRequired = state.tutorialStep === 1 ? 'mint' : state.tutorialStep === 3 ? 'rally' : '';
  if (!state.tutorialComplete && type !== tutorialRequired) return { ok: false, reason: 'ลูมี่จะปลดล็อกอาคารนี้ตามขั้นตอนบทสอน' };
  if (hallLevel(state) < def.unlockHall) return { ok: false, reason: `ปลดล็อกที่ Spirit Hall เลเวล ${def.unlockHall}` };
  const count = state.buildings.filter(item => item.type === type).length;
  const limit = buildingLimit(type, hallLevel(state));
  if (count >= limit) return { ok: false, reason: `สร้าง ${def.name} ได้สูงสุด ${limit} หลังที่ Hall เลเวลนี้` };
  if (!options.instant && builderStatus(state).available <= 0) return { ok: false, reason: 'ช่างทุกคนกำลังทำงานอยู่' };
  if (!canAfford(state, cost)) return { ok: false, reason: 'ทรัพยากรสำหรับสร้างยังไม่พอ' };
  const used = new Set(state.buildings.map(item => `${item.x},${item.z}`));
  const slots = state.territoryLevel ? BUILD_SLOTS : BUILD_SLOTS.slice(0, 9);
  const slot = slots.find(position => !used.has(`${position[0]},${position[1]}`));
  if (!slot) return { ok: false, reason: 'พื้นที่เต็ม — ขยายเกาะก่อน' };
  spend(state, cost);
  const building = { id: `${type}-${count + 1}`, type, level: 1, x: slot[0], z: slot[1], rotation: 0 };
  if (!options.instant) {
    building.underConstruction = 'build';
    building.readyAt = Date.now() + constructionDuration(state, 1, 'build');
    state.constructionQueue.push({ buildingId: building.id, kind: 'build', readyAt: building.readyAt });
  }
  state.buildings.push(building);
  state.xp += 35;
  return { ok: true, building, queued: !options.instant };
}

export function moveBuilding(state, id, x, z, rotation = 0) {
  const building = state.buildings.find(item => item.id === id);
  if (!building || building.type === 'hall') return { ok: false, reason: 'ย้ายอาคารนี้ไม่ได้' };
  const snapped = { x: Math.round(Number(x) * 2) / 2, z: Math.round(Number(z) * 2) / 2 };
  const radius = state.territoryLevel ? 31 : 10;
  if (!Number.isFinite(snapped.x) || !Number.isFinite(snapped.z) || Math.hypot(snapped.x, snapped.z) > radius) return { ok: false, reason: 'ตำแหน่งอยู่นอกพื้นที่เกาะ' };
  const overlap = state.buildings.some(item => item.id !== id && Math.hypot(item.x - snapped.x, item.z - snapped.z) < (BUILDINGS[item.type].size + BUILDINGS[building.type].size) * .42);
  if (overlap) return { ok: false, reason: 'ตำแหน่งนี้ชนอาคารอื่น' };
  Object.assign(building, snapped, { rotation: ((Math.round(rotation) % 4) + 4) % 4 });
  return { ok: true, building };
}

export function layoutSlotCount(state) {
  return state.monetization?.entitlements?.includes('layout_slots_3') ? 4 : 1;
}

export function saveBaseLayout(state, slot = 0) {
  if (slot < 0 || slot >= layoutSlotCount(state)) return { ok: false, reason: 'ยังไม่ได้ปลดล็อกช่องแปลนนี้' };
  state.baseLayouts ||= [];
  state.baseLayouts[slot] = state.buildings.map(({ id, x, z, rotation = 0 }) => ({ id, x, z, rotation }));
  return { ok: true, slot };
}

export function loadBaseLayout(state, slot = 0) {
  const layout = state.baseLayouts?.[slot];
  if (!layout) return { ok: false, reason: 'ช่องนี้ยังไม่มีแปลนที่บันทึกไว้' };
  const byId = new Map(layout.map(item => [item.id, item]));
  for (const building of state.buildings) {
    const saved = byId.get(building.id);
    if (saved) Object.assign(building, { x: saved.x, z: saved.z, rotation: saved.rotation || 0 });
  }
  return { ok: true, slot };
}

function pickCharacter(rarity, random) {
  const pool = Object.keys(CHARACTERS).filter(id => CHARACTERS[id].rarity === rarity);
  return pool[Math.floor(random() * pool.length) % pool.length];
}

export function summonOne(state, options = {}) {
  const random = options.random || Math.random;
  const free = options.free || false;
  if (!free && state.summonTickets <= 0 && state.skyDew < 120) return { ok: false, reason: 'น้ำค้างฟ้าหรือตั๋วเรียกไม่พอ' };
  if (!free) {
    if (state.summonTickets > 0) state.summonTickets -= 1;
    else state.skyDew -= 120;
  }
  state.summonPity += 1;
  state.srPity += 1;
  let rarity = options.guaranteedRarity || '';
  if (!rarity && state.summonPity >= 50) rarity = 'UR';
  if (!rarity && state.srPity >= 10) rarity = 'SR';
  if (!rarity) {
    const roll = random() * 100;
    let cursor = 0;
    rarity = SUMMON_ODDS.find(entry => { cursor += entry.chance; return roll < cursor; })?.rarity || 'C';
  }
  if (RARITIES[rarity].rank >= RARITIES.SR.rank) state.srPity = 0;
  if (rarity === 'UR') state.summonPity = 0;
  const forcedId = options.characterId && CHARACTERS[options.characterId]?.rarity === rarity ? options.characterId : '';
  const id = forcedId || pickCharacter(rarity, random);
  const definition = CHARACTERS[id];
  const owned = state.heroes[id];
  const isNew = !owned;
  if (isNew) state.heroes[id] = { level: 1, shards: 0, copies: 1 };
  else {
    owned.copies += 1;
    owned.shards += RARITIES[rarity].shardReward;
  }
  const current = activeCharacter(state, definition.type);
  if (!state.activeHeroes[definition.type] || RARITIES[rarity].rank > RARITIES[current?.rarity || 'C'].rank) state.activeHeroes[definition.type] = id;
  return { ok: true, id, character: definition, rarity, isNew, shards: isNew ? 0 : RARITIES[rarity].shardReward };
}

export function summonMany(state, count = 10, options = {}) {
  const cost = 1080;
  if (!options.free && state.skyDew < cost) return { ok: false, reason: 'ต้องใช้น้ำค้างฟ้า 1,080' };
  if (!options.free) state.skyDew -= cost;
  const results = [];
  for (let index = 0; index < count; index += 1) results.push(summonOne(state, { ...options, free: true, guaranteedRarity: index === count - 1 && state.srPity >= 9 ? 'SR' : '' }));
  return { ok: true, results };
}

export function upgradeHero(state, id) {
  const owned = state.heroes[id];
  if (!owned) return { ok: false, reason: 'ยังไม่มีตัวละครนี้' };
  if (owned.level >= 20) return { ok: false, reason: 'เลเวลสูงสุดแล้ว' };
  const shards = 8 + owned.level * 4;
  const gold = 120 + owned.level * 90;
  if (owned.shards < shards || state.gold < gold) return { ok: false, reason: `ต้องใช้ ${shards} shard และ ${gold} ทอง` };
  owned.shards -= shards;
  state.gold -= gold;
  owned.level += 1;
  return { ok: true, level: owned.level };
}

export function applyBattleReward(state, percent, marks, options = {}) {
  const key = String(options.opponentId || options.opponentName || 'practice');
  const repeats = Math.max(0, state.recentOpponents?.[key] || 0);
  const repeatMultipliers = GAME_RULES.battle.repeatOpponentRewardMultipliers;
  const repeatMultiplier = options.practice ? GAME_RULES.battle.practiceRewardMultiplier : repeatMultipliers[Math.min(repeats, repeatMultipliers.length - 1)];
  const hall = hallLevel(state);
  const base = { gold: percent * (4 + hall) + marks * 90, wood: percent * (3 + hall * .6) + marks * 65, dew: marks * (10 + hall * 3) };
  const reward = { gold: Math.round(base.gold * repeatMultiplier), wood: Math.round(base.wood * repeatMultiplier), dew: Math.round(base.dew * repeatMultiplier) };
  const caps = storageCaps(state);
  reward.gold = Math.max(0, Math.min(reward.gold, caps.gold - state.gold));
  reward.wood = Math.max(0, Math.min(reward.wood, caps.wood - state.spiritWood));
  reward.dew = Math.max(0, Math.min(reward.dew, caps.dew - state.skyDew));
  state.gold += reward.gold;
  state.spiritWood += reward.wood;
  state.skyDew += reward.dew;
  state.xp += Math.round(percent * .8 + marks * 20);
  state.level = Math.max(state.level, 1 + Math.floor(state.xp / 180));
  if (marks > 0) state.battleWins += 1;
  state.recentOpponents = { ...(state.recentOpponents || {}), [key]: repeats + 1 };
  return { ...reward, multiplier: repeatMultiplier };
}

export function grantTutorialReward(state, step) {
  const rewards = {
    1: { gold: 220, wood: 180 }, 2: { gold: 180, wood: 150 }, 3: { wood: 180, dew: 60 },
    4: { wood: 260 }, 6: { gold: 900, wood: 700, dew: 80 }, 7: { gold: 900, wood: 700, dew: 50 }
  };
  const reward = rewards[step] || {};
  state.gold += reward.gold || 0;
  state.spiritWood += reward.wood || 0;
  state.skyDew += reward.dew || 0;
  return reward;
}

export function resetState() { localStorage.removeItem(STORAGE_KEY); return freshState(); }

export function toBaseSnapshot(state) {
  return { ownerName: state.playerName, ownerCode: state.online.friendCode || '', territoryLevel: state.territoryLevel,
    buildings: state.buildings.map(building => ({ kind: BUILDINGS[building.type].kind, level: building.level, x: building.x, z: building.z })) };
}

export function toCloudProfile(state) {
  return { version: 3, gold: Math.floor(state.gold), spiritWood: Math.floor(state.spiritWood), skyDew: Math.floor(state.skyDew), homeBase: toBaseSnapshot(state),
    heroes: state.heroes, activeHeroes: state.activeHeroes, summonPity: state.summonPity, monetization: state.monetization, adventure: state.adventure, constructionQueue: state.constructionQueue, baseLayouts: state.baseLayouts, trainingQueue: [] };
}
